import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import { useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import Preloader from "../Preloader";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useSnackbar } from "notistack";
import Chip from "@mui/material/Chip";
import { ShoppingCart } from "@mui/icons-material";
import { licenseFormatTypeEnum } from "../../utils/licenseFormatTypes";

function findContactsPreferredAddress(deliveryAddresses, contactActorId) {
  if (!deliveryAddresses || deliveryAddresses.length === 0 || !contactActorId) return null;

  return deliveryAddresses.find((adr) => adr.preferredAddresses.some((pa) => pa.actorId == contactActorId))
    ?.actorAddressId;
}

export default function CardPrintAtMaSystemDialog({
  open,
  onClose,
  isGenericActorLicenses,
  licenseFormatType,
  extraRequestData,
}) {
  const [addresses, setAddresses] = useState([]);
  const [tempAddress, setTempAddress] = useState({});
  const [useTempAddress, setUseTempAddress] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [customerOrderNo, setCustomerOrderNo] = useState(null);
  const [extraNotification, setExtraNotification] = useState(null);
  const [companyDefaultCustomerOrderNo, setCompanyDefaultCustomerOrderNo] = useState(null);
  const [isPrepay, setIsPrepay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const api = useApi();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      api
        .fetch(`${import.meta.env.VITE_MAIN_URL}licenseprint/card/masystem/info`, false, "GET")
        .then((response) => {
          if (response && response.isSuccessful) {
            setAddresses(response.deliveryAddresses);
            if (!response?.deliveryAddresses || response.deliveryAddresses.length < 1) {
              setUseTempAddress(true);
            }

            setContacts(response.contacts);
            setCompanyDefaultCustomerOrderNo(response.defaultCustomerOrderNo);

            let defaultCustomerOrderNo = response.defaultCustomerOrderNo;
            let preferredActorAddressId;
            if (response.contacts?.length > 0) {
              const firstContactActorId = response.contacts[0].actorId;
              setSelectedContactId(firstContactActorId);
              defaultCustomerOrderNo = response.contacts[0].defaultCustomerOrderNo ?? defaultCustomerOrderNo;
              preferredActorAddressId = findContactsPreferredAddress(response.deliveryAddresses, firstContactActorId);
            }
            if (response.deliveryAddresses?.length > 0) {
              setSelectedAddressId(preferredActorAddressId ?? response.deliveryAddresses[0].actorAddressId);
            }
            setCustomerOrderNo(defaultCustomerOrderNo);
            setIsPrepay(response.isPrepay);
          }
        })
        .finally((_) => setIsLoading(false));
    }
  }, [open, api]);

  async function sendPrintJobToMaSystem() {
    const dto = {
      deliveryAddressId: useTempAddress ? 0 : selectedAddressId,
      contactActorId: selectedContactId,
      customerOrderNo,
      licenseFormatType,
      extraNotification,
      tempAddress: useTempAddress ? tempAddress : null,
      ...extraRequestData,
    };

    const url = isGenericActorLicenses
      ? `${import.meta.env.VITE_MAIN_URL}license-registration/print`
      : `${import.meta.env.VITE_MAIN_URL}licenseprint/card/masystem/print`;
    const response = await api.fetch(url, dto);

    if (response && response.isSuccessful) {
      const plasticOnly = "OrderOk";
      const digitalOnly = isPrepay ? "OrderOkDigitalOnlyPrepay" : "OrderOkDigitalOnly";
      const digitalAndPlastic = isPrepay ? "OrderOkPlasticAndDigitalPrepay" : "OrderOkPlasticAndDigital";
      const messageTranslationKey =
        licenseFormatType === licenseFormatTypeEnum.physical
          ? plasticOnly
          : licenseFormatType === licenseFormatTypeEnum.digital
            ? digitalOnly
            : digitalAndPlastic;
      const successMessage = enqueueSnackbar(Translate.get(messageTranslationKey), {
        variant: "success",
        autoHideDuration: 12000,
        onClick: () => closeSnackbar(successMessage),
      });
      const orderDetails = { orderId: response.orderId, orderLineId: response.orderLineId };
      onClose(true, response && response.isSuccessful, orderDetails, messageTranslationKey);
    }
  }

  function handleTempAddressChange(event, fieldName) {
    const newValue = event.target.value;
    const tempAddressCopy = { ...tempAddress };
    tempAddressCopy[fieldName] = newValue;
    setTempAddress(tempAddressCopy);
  }

  function handleContactSelected(event) {
    const previousContact = contacts.find((c) => c.actorId === selectedContactId);
    if (previousContact.defaultCustomerOrderNo === customerOrderNo) {
      setCustomerOrderNo(companyDefaultCustomerOrderNo);
    }

    const actorId = event.target.value;
    const contact = contacts.find((c) => c.actorId === actorId);
    if (contact.defaultCustomerOrderNo) {
      setCustomerOrderNo(contact.defaultCustomerOrderNo);
    }

    const preferredActorAddressId = findContactsPreferredAddress(addresses, actorId);
    if (preferredActorAddressId) {
      setSelectedAddressId(preferredActorAddressId);
    }

    setSelectedContactId(actorId);
  }

  return (
    <>
      {open && isLoading && <Preloader zIndexOverride={3000} />}
      {open && !isLoading && (
        <Dialog maxWidth="sm" scroll="paper" onClose={() => onClose(false)} open={open}>
          <DialogTitle>
            {licenseFormatType === licenseFormatTypeEnum.physical
              ? Translate.get("OrderMaUtbildningCardPrint")
              : licenseFormatType === licenseFormatTypeEnum.digital
                ? Translate.get("OrderMaUtbildningDigital")
                : Translate.get("OrderMaUtbildningCardPrintAndDigital")}
          </DialogTitle>
          <DialogContent sx={{ paddingBottom: "24px", minWidth: { sm: "590px" } }}>
            <Grid container direction="column" spacing={2}>
              <Grid>
                <FormControl fullWidth sx={{ marginTop: "10px" }}>
                  <InputLabel id="contact-select-label" required>
                    {Translate.get("Contact")}
                  </InputLabel>
                  <Select
                    required
                    labelId="contact-select-label"
                    id="contact-select"
                    value={selectedContactId ?? ""}
                    label={Translate.get("Contact")}
                    onChange={handleContactSelected}
                    disabled={!contacts || contacts.length === 0}
                  >
                    {contacts &&
                      contacts.map((contact) => (
                        <MenuItem key={contact.actorId} value={contact.actorId}>
                          <div>
                            <div>{contact.actorName}</div>
                            <div>{contact?.actorEmail ? contact?.actorEmail : ""}</div>
                          </div>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <TextField
                  fullWidth
                  value={customerOrderNo}
                  InputProps={{ spellCheck: false }}
                  onChange={(event) => setCustomerOrderNo(event.target.value)}
                  label={Translate.get("YourOrderNo")}
                  inputProps={{ maxLength: 30 }}
                />
              </Grid>
              <Grid>
                <TextField
                  fullWidth
                  value={extraNotification}
                  InputProps={{ spellCheck: false }}
                  onChange={(event) => setExtraNotification(event.target.value)}
                  label={Translate.get("ExtraInvoiceDetails")}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              {(licenseFormatType === licenseFormatTypeEnum.physical ||
                licenseFormatType === licenseFormatTypeEnum.both) && (
                <>
                  <Grid>{/* Empty item for some space */}</Grid>
                  <Grid>
                    <FormControl fullWidth>
                      <InputLabel id="address-select-label" required>
                        {Translate.get("Address")}
                      </InputLabel>
                      <Select
                        required
                        sx={{ height: "105px" }}
                        multiline
                        rows={3}
                        labelId="address-select-label"
                        id="address-select"
                        value={useTempAddress ? -1 : (selectedAddressId ?? "")}
                        label={Translate.get("Address")}
                        onChange={(event) => setSelectedAddressId(event.target.value)}
                        disabled={useTempAddress || !addresses || addresses.length === 0}
                      >
                        {!useTempAddress &&
                          addresses &&
                          addresses.map((address) => (
                            <MenuItem divider key={address.actorAddressId} value={address.actorAddressId}>
                              <Grid container>
                                <Grid size={9}>{address.actorAddressStreet1}</Grid>
                                <Grid size={3}>
                                  <span
                                    style={{
                                      backgroundColor: "#EDEFF1",
                                      padding: "3px",
                                      borderRadius: "3px",
                                      fontSize: "smaller",
                                    }}
                                  >
                                    {Translate.getActorAddressType(address.actorAddressType)}
                                  </span>
                                </Grid>
                                <Grid size={12}>{address.actorAddressStreet2}</Grid>
                                <Grid size={12}>
                                  {address.actorAddressZipCode} {address.actorAddressCity}
                                </Grid>
                              </Grid>
                            </MenuItem>
                          ))}
                        {useTempAddress && (
                          <MenuItem value={-1}>
                            <Grid container direction="column">
                              <Grid>{tempAddress.street1}</Grid>
                              <Grid>{tempAddress.street2}</Grid>
                              <Grid>
                                {tempAddress.cityCode} {tempAddress.city}
                              </Grid>
                            </Grid>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useTempAddress}
                          onChange={(event) => setUseTempAddress(event.target.checked)}
                        />
                      }
                      label={Translate.get("UseTemporaryAddress")}
                    />
                  </Grid>
                </>
              )}
              {useTempAddress && (
                <Grid container spacing={2} direction={{ xs: "column", sm: "row" }}>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      required
                      value={tempAddress.street1 ?? ""}
                      label={Translate.get("Name")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) => handleTempAddressChange(event, "street1")}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      required
                      value={tempAddress.street2 ?? ""}
                      label={Translate.get("StreetAddress")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) => handleTempAddressChange(event, "street2")}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      required
                      value={tempAddress.cityCode ?? ""}
                      label={Translate.get("ZipCode")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) => handleTempAddressChange(event, "cityCode")}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      required
                      value={tempAddress.city ?? ""}
                      label={Translate.get("City")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) => handleTempAddressChange(event, "city")}
                    />
                  </Grid>
                  {/* 
                  //Mårten 2023-11-23 tagit bort land eftersom det ställer till problem vid fakturering
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      value={tempAddress.country ?? ""}
                      label={Translate.get("Country")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) =>
                        handleTempAddressChange(event, "country")
                      }
                    />
                  </Grid> */}
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      value={tempAddress.referenceText ?? ""}
                      label={Translate.get("Attention")}
                      InputProps={{ spellCheck: false }}
                      onChange={(event) => handleTempAddressChange(event, "referenceText")}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose(false)}>{Translate.get("Cancel")}</Button>
            <ButtonWithSpinner
              variant="contained"
              disabled={
                (licenseFormatType !== licenseFormatTypeEnum.digital && useTempAddress
                  ? !(
                      tempAddress &&
                      tempAddress.street1 &&
                      tempAddress.street2 &&
                      tempAddress.cityCode &&
                      tempAddress.city
                    )
                  : !selectedAddressId) ||
                !selectedContactId ||
                !extraRequestData
              }
              onClick={sendPrintJobToMaSystem}
              endIcon={<ShoppingCart />}
            >
              {Translate.get("PlaceOrder")}
            </ButtonWithSpinner>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
